import { User } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { ZL_USERINFO } from '@/utils/constants';
import { env } from '@/utils/env';
import md5 from 'crypto-js/md5';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface CriteoConfig {
  account: number;
  isEnabled?: boolean;
}

// Define Criteo queue interface
interface CriteoEvent {
  event: string;
  [key: string]: any;
}

// Window interface已在index.d.ts中统一定义

export class CriteoProvider implements Provider {
  type = ProviderType.CRITEO;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: CriteoConfig;

  constructor(config: CriteoConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Load Criteo script
      // 为了减小对TBT的影响，把其加载的时序再往后延
      setTimeout(() => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = '//static.criteo.net/js/ld/ld.js';
        script.async = true;

        script.onload = () => {
          if (this.loggingEnabled) {
            logger.log('Criteo: Script loaded successfully');
          }
        };

        script.onerror = () => {
          if (this.loggingEnabled) {
            logger.error('Failed to load Criteo script');
          }
        };

        document.head.appendChild(script);
      }, 5000);
      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Criteo:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (this.loggingEnabled) {
        logger.log('Criteo tracking event:', event);
      }

      // Initialize criteo_q if not exists
      window.criteo_q = window.criteo_q || [];

      // Detect device type
      const deviceType = this.getDeviceType();

      // Get user email hash
      const hashedEmail = this.getHashedEmail();

      // Push initial configuration events
      window.criteo_q.push(
        {
          event: 'setAccount',
          account: this.config.account,
        },
        {
          event: 'setHashedEmail',
          email: hashedEmail,
        },
        {
          event: 'setSiteType',
          type: deviceType,
        },
        {
          event: event.eventName || 'custom',
          ...(event.properties as unknown as CriteoEvent),
        },
      );
    } catch (error) {
      logger.error('Criteo tracking error:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private getDeviceType(): 'd' | 't' | 'm' {
    if (typeof navigator === 'undefined') return 'd';

    // eslint-disable-next-line prefer-destructuring
    const userAgent = navigator.userAgent;

    // Check for iPad first
    if (/iPad/.test(userAgent)) {
      return 't'; // tablet
    }

    // Check for mobile devices
    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(userAgent)) {
      return 'm'; // mobile
    }

    // Default to desktop
    return 'd'; // desktop
  }

  /**
   * 启用Criteo日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Criteo日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  private getHashedEmail(): string {
    try {
      const userInfoStr = localStorage.getItem(ZL_USERINFO) || '{}';
      const userInfo: User = JSON.parse(userInfoStr);
      if (userInfo?.email) {
        // Use crypto-js MD5 hash
        return md5(userInfo.email).toString();
      }
    } catch (error) {
      logger.error('Failed to get user email:', error);
    }

    return '';
  }
}
